// @ts-check
// Parte de Configurações > Canais da frente de leads e WhatsApp (contrato 08): páginas do Meta Lead Ads. A conexão do
// WhatsApp pela Meta fica em meta-signup/ e os modelos na tela própria (whatsapp-templates/).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { CopyableValue } from './settings-channels-page.js';
import { FormError, SelectField, TextField, describeError } from './integration-ui.js';
import { connectLeadAdsPage, disconnectLeadAdsPage, listCaptureForms, listLeadAdsPages, updateLeadAdsPage } from '../../data/integrations.js';
import { formatDateTime } from '../../lib/format.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/integrations.js').LeadAdsPage} LeadAdsPage
 */

/**
 * @param {{ isAdmin: boolean }} props
 */
export function LeadAdsCard({ isAdmin }) {
  const [pages, setPages] = useState(/** @type {LeadAdsPage[] | null} */ (null));
  const [webhookPath, setWebhookPath] = useState('/webhooks/meta-leads');
  const [forms, setForms] = useState(/** @type {Array<{ value: string, label: string }>} */ ([]));
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [pageId, setPageId] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [captureFormId, setCaptureFormId] = useState('');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const load = () => {
    if (!isAdmin) return;
    listLeadAdsPages().then((result) => {
      setPages(result.pages);
      setWebhookPath(result.webhookPath);
    }).catch(() => setPages([]));
  };
  useEffect(() => {
    load();
    if (isAdmin) listCaptureForms().then((list) => setForms(list.map((form) => ({ value: form.id, label: form.name })))).catch(() => setForms([]));
  }, []);
  const formOptions = [{ value: '', label: t('leadCapture.leadAds.noForm') }, ...forms];
  const connect = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await connectLeadAdsPage({ pageId: pageId.trim(), accessToken: accessToken.trim(), captureFormId: captureFormId || null });
      showSuccessSnackbar(t('leadCapture.leadAds.connectedSnackbar'));
      setIsFormOpen(false);
      setPageId('');
      setAccessToken('');
      load();
    } catch (requestError) {
      setError(describeError(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="channel-card">
    <div class="channel-card-header">
      <${Icon} name="IconSpeakerphone" size=${24} />
      <div class="channel-card-titles">
        <span class="channel-card-title">${t('leadCapture.leadAds.title')}</span>
        <span class="channel-card-description">${t('leadCapture.leadAds.description')}</span>
      </div>
      <span class="channel-status" data-connected=${pages && pages.length > 0 ? 'true' : 'false'}>
        <span class="channel-status-dot"></span>${pages && pages.length > 0 ? t('leadCapture.leadAds.pagesConnected', { count: pages.length }) : t('settings.channels.whatsapp.disconnected')}
      </span>
    </div>
    ${!isAdmin ? html`<p class="channel-card-note"><${Icon} name="IconLock" size=${14} />${t('leadCapture.common.adminOnly')}</p>` : null}
    ${isAdmin && pages === null ? html`<${Skeleton} height=${40} radius=${8} />` : null}
    ${isAdmin && pages && pages.length > 0
      ? html`<div class="template-list">
          ${pages.map((page) => html`<div key=${page.pageId} class="lead-ads-page">
            <div class="channel-card-titles">
              <span class="channel-card-title">${page.pageName || page.pageId}</span>
              <span class="integration-muted-small">${t('leadCapture.leadAds.pageId')}: ${page.pageId} · ${t('leadCapture.leadAds.since', { date: formatDateTime(page.createdAt) })}</span>
            </div>
            <select class="text-input integration-select lead-ads-form-select" aria-label=${t('leadCapture.leadAds.form')} value=${page.captureFormId ?? ''}
              onChange=${async (/** @type {Event} */ event) => {
                try {
                  await updateLeadAdsPage(page.pageId, /** @type {HTMLSelectElement} */ (event.currentTarget).value || null);
                  showSuccessSnackbar(t('leadCapture.common.saved'));
                  load();
                } catch (requestError) {
                  showErrorSnackbar(requestError);
                }
              }}>
              ${formOptions.map((option) => html`<option key=${option.value} value=${option.value} selected=${option.value === (page.captureFormId ?? '')}>${option.label}</option>`)}
            </select>
            <${LightIconButton} icon="IconUnlink" ariaLabel=${t('leadCapture.leadAds.disconnect')} onClick=${async () => {
              try {
                await disconnectLeadAdsPage(page.pageId);
                load();
              } catch (requestError) {
                showErrorSnackbar(requestError);
              }
            }} />
          </div>`)}
        </div>`
      : null}
    ${isAdmin && isFormOpen
      ? html`<div class="channel-form">
          <div class="form-field-row">
            <${TextField} label=${t('leadCapture.leadAds.pageId')} value=${pageId} onInput=${setPageId} placeholder="102345678901234" isRequired=${true} />
            <${SelectField} label=${t('leadCapture.leadAds.form')} value=${captureFormId} onChange=${setCaptureFormId} options=${formOptions} hint=${t('leadCapture.leadAds.formHint')} />
          </div>
          <${TextField} label=${t('leadCapture.leadAds.accessToken')} value=${accessToken} onInput=${setAccessToken} placeholder="EAAG..." type="password" isRequired=${true}
            hint=${t('leadCapture.leadAds.accessTokenHint')} />
          <${FormError} message=${error} />
          <div class="channel-form-actions">
            <${Button} title=${t('common.cancel')} variant="secondary" size="small" onClick=${() => setIsFormOpen(false)} />
            <${Button} title=${t('leadCapture.leadAds.connect')} icon="IconPlugConnected" accent="blue" size="small" isLoading=${isSaving}
              disabled=${!pageId.trim() || !accessToken.trim()} onClick=${connect} />
          </div>
        </div>`
      : null}
    ${isAdmin && !isFormOpen ? html`<div class="channel-card-actions"><${Button} title=${t('leadCapture.leadAds.add')} icon="IconPlus" variant="secondary" size="small" onClick=${() => setIsFormOpen(true)} /></div>` : null}
    ${isAdmin
      ? html`<details class="channel-help">
          <summary>${t('leadCapture.leadAds.helpTitle')}</summary>
          <ol>${[1, 2, 3, 4].map((step) => html`<li key=${step}>${t(`leadCapture.leadAds.helpSteps.step${step}`)}</li>`)}</ol>
          <span class="form-field-label">${t('settings.channels.whatsapp.webhookUrl')}</span>
          <${CopyableValue} value=${`${window.location.origin}${webhookPath}`} label=${t('settings.channels.whatsapp.webhookUrl')} />
        </details>`
      : null}
  </div>`;
}
